import React from 'react';
import { motion } from 'framer-motion';
import { MapPin } from 'lucide-react';

const nodes = [
  { name: 'Mumbai', x: 68, y: 55, primary: true },
  { name: 'Bangalore', x: 70, y: 63, primary: true },
  { name: 'Delhi', x: 69, y: 44 },
  { name: 'Singapore', x: 78, y: 62 },
  { name: 'Dubai', x: 62, y: 50 },
  { name: 'Frankfurt', x: 50, y: 32 },
  { name: 'London', x: 46, y: 30 },
  { name: 'New York', x: 26, y: 38 },
  { name: 'Virginia', x: 27, y: 42 },
  { name: 'Tokyo', x: 85, y: 40 },
  { name: 'Sydney', x: 88, y: 78 },
  { name: 'São Paulo', x: 33, y: 72 }
];

const WorldMap = () => {
  return (
    <section className="section-padding bg-gradient-to-b from-[#050914] to-slate-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-15" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-cyan-500/10 blur-[120px] rounded-full" />

      <div className="container-x relative">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="text-xs font-semibold tracking-widest text-cyan-400 uppercase mb-3">Global Coverage</div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-white">Datacenters on <span className="bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">6 continents</span></h2>
          <p className="mt-4 text-slate-300">Serve every customer with sub-50ms latency through our globally distributed edge network.</p>
        </div>

        <div className="relative aspect-[2/1] max-w-5xl mx-auto rounded-3xl bg-slate-900/50 backdrop-blur border border-cyan-400/20 p-6 overflow-hidden">
          {/* Dotted map background */}
          <svg viewBox="0 0 100 50" className="absolute inset-0 w-full h-full opacity-25" preserveAspectRatio="none">
            {Array.from({ length: 30 }).map((_, r) =>
              Array.from({ length: 60 }).map((_, c) => {
                const x = (c / 60) * 100;
                const y = (r / 30) * 50;
                // A rough continent mask so it looks like a world map
                const on =
                  (x > 15 && x < 40 && y > 15 && y < 45) || // Americas
                  (x > 42 && x < 58 && y > 12 && y < 35) || // Europe/Africa
                  (x > 60 && x < 90 && y > 15 && y < 42) || // Asia
                  (x > 80 && x < 95 && y > 35 && y < 45);   // Oceania
                return on ? <circle key={`${r}-${c}`} cx={x} cy={y} r="0.35" fill="#22d3ee" /> : null;
              })
            )}
          </svg>

          {/* Connection lines */}
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
            {nodes.slice(1).map((n, i) => {
              const src = nodes[0];
              return (
                <motion.line
                  key={i}
                  x1={src.x} y1={src.y} x2={n.x} y2={n.y}
                  stroke="url(#lineGrad)" strokeWidth="0.15"
                  strokeDasharray="1,1"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 0.8 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.6 }}
                />
              );
            })}
            <defs>
              <linearGradient id="lineGrad" x1="0" x2="1">
                <stop offset="0" stopColor="#22d3ee" />
                <stop offset="1" stopColor="#0ea5e9" stopOpacity="0.3" />
              </linearGradient>
            </defs>
          </svg>

          {/* Nodes */}
          {nodes.map((n, i) => (
            <motion.div
              key={n.name}
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 + i * 0.06 }}
              className="absolute -translate-x-1/2 -translate-y-1/2 group cursor-pointer"
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
            >
              <div className="relative">
                {n.primary && (
                  <span className="absolute inset-0 -m-2 rounded-full bg-cyan-400/40 animate-ping" />
                )}
                <div className={`w-3 h-3 rounded-full ${n.primary ? 'bg-cyan-300' : 'bg-cyan-400'} shadow-[0_0_10px_#22d3ee]`} />
                <div className="absolute top-4 left-1/2 -translate-x-1/2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="px-2 py-1 rounded-md bg-cyan-500 text-white text-[10px] font-bold flex items-center gap-1">
                    <MapPin size={10} /> {n.name}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Legend */}
          <div className="absolute bottom-4 left-4 right-4 flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-1.5 text-cyan-300">
              <span className="w-2 h-2 rounded-full bg-cyan-300" /> Primary DC
            </div>
            <div className="flex items-center gap-1.5 text-cyan-300/70">
              <span className="w-2 h-2 rounded-full bg-cyan-400" /> Edge PoP
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
          {[
            { v: '12+', l: 'Locations' },
            { v: '6', l: 'Continents' },
            { v: '<50ms', l: 'Avg Latency' },
            { v: '100Gbps', l: 'Backbone' }
          ].map((k) => (
            <div key={k.l} className="p-4 rounded-2xl bg-white/5 border border-white/10 backdrop-blur text-center">
              <div className="text-2xl md:text-3xl font-black bg-gradient-to-r from-cyan-300 to-sky-400 bg-clip-text text-transparent">{k.v}</div>
              <div className="text-xs text-slate-400 uppercase tracking-widest mt-1">{k.l}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WorldMap;
